<template>
  <div class="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <h1 class="text-3xl font-bold text-gray-900 mb-8">常见问题</h1>
      
      <!-- Tabs 标签页 -->
      <div class="mb-8">
        <!-- Tabs 头部导航 -->
        <div class="flex border-b border-gray-200 overflow-x-auto whitespace-nowrap">
          <button
            v-for="(tab, index) in tabs"
            :key="index"
            @click="handleTabChange(index)"
            class="px-4 py-3 text-sm font-medium transition-colors duration-200 focus:outline-none"
            :class="{
              'text-blue-600 border-b-2 border-blue-600': activeTab === index,
              'text-gray-500 hover:text-gray-700': activeTab !== index
            }"
          >
            {{ tab.name }}
          </button>
        </div>
        
        <!-- 对应Tab的内容 -->
        <div class="mt-4">
          <!-- Collapse 折叠面板 -->
          <div class="space-y-0.5">
            <div 
              v-for="(item, index) in activeTabData"
              :key="index"
              class="border border-gray-200 rounded-md overflow-hidden transition-all duration-200"
              :class="{ 'shadow-md': expandedIndex === index }"
            >
              <!-- 折叠面板头部 -->
              <button
                type="button"
                @click="toggleAccordion(index)"
                class="w-full flex justify-between items-center px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                :class="{ 'rounded-b-none': expandedIndex === index }"
              >
                <span class="text-lg font-medium text-gray-800">{{ item.question }}</span>
                <svg 
                  class="h-5 w-5 text-gray-500 transition-transform duration-200 ease-in-out"
                  :class="{ 'transform rotate-180': expandedIndex === index }"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
              
              <!-- 折叠面板内容 -->
              <transition name="collapse">
                <div v-if="expandedIndex === index" class="px-6 py-4 bg-gray-50 border-t border-gray-200 text-left">
                  <p class="text-gray-600 leading-relaxed whitespace-pre-line">{{ item.answer }}</p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

// FAQ分类Tabs数据
const tabs = ref([
  {
    name: '企业',
    data: [
      {
        question: "北境骑团集团的使命是什么？",
        answer: "我们的使命是\"并肩前行，探索骑行乐趣\"。"
      },
      {
        question: "公司为什么命名为\"北境骑团\"？",
        answer: "北境骑团意谓\"并肩前行，探索骑行乐趣\"，喻意我们的平台为骑行爱好者开启财富之门。"
      }
    ]
  },
  {
    name: '产品服务',
    data: [
      {
        question: "北境骑团提供哪些核心产品和服务？",
        answer: "自行车、头盔、服装"
      }
    ]
  }
  
]);

// 响应式数据 - 当前激活的Tab索引
const activeTab = ref(0);

// 响应式数据 - 当前展开的问题索引
const expandedIndex = ref(-1);

// 计算属性 - 获取当前激活Tab的数据
const activeTabData = computed(() => {
  return tabs.value[activeTab.value].data || [];
});

// 方法 - 切换折叠面板状态
const toggleAccordion = (index: number) => {
  expandedIndex.value = expandedIndex.value === index ? -1 : index;
};

// 监听Tab变化，重置折叠面板状态
const handleTabChange = (index: number) => {
  activeTab.value = index;
  expandedIndex.value = -1; // 切换Tab时重置展开状态
};
</script>

<style scoped>
/* 折叠动画 */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* 按钮交互效果 */
button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(229, 231, 235, 0.5);
}

/* 相邻面板的间距控制 */
.space-y-0\.5 > * + * {
  margin-top: 0.125rem;
}
</style>